import { format, isBefore } from "date-fns";
import { inject, observer } from "mobx-react";
import React, { FC } from "react";
import { buildStyles, CircularProgressbarWithChildren } from "react-circular-progressbar";
import { useTranslation } from "react-i18next";
import Box from "react-raster";
import { Avatar, Icon, IconId, Tooltip, Text} from "src/components";
import { StatusAvatar } from "src/pages/room/editData";
import { DeliverableStore, MeetingStore, TagStore, UserStore } from "src/stores";
import { DataType, IItem, ITag } from "src/types";
import { getTextColorFromBackgroundColor } from "src/util/util";
import styled, { css, FlattenSimpleInterpolation } from "styled-components";
import { theme } from "../config/theme";
import { Deliverable, Item, Milestone, Question, Tag } from "../models";

export interface ICardProps {
  cols?:         number;
  data:          Item<IItem> | Question | Deliverable | Milestone;
  iconId?:       IconId;
  meetingStore?: MeetingStore;
  type?:         DataType;
  userStore?:    UserStore;
  delivStore?:   DeliverableStore;
  tagStore?:     TagStore;
  onClickIcon?: () => void;
  onClick?:     () => void;
}

export const Card: FC<ICardProps> = inject("userStore", "meetingStore", "delivStore", "tagStore")(observer((props: ICardProps): JSX.Element => {
  const { data, type, onClick, tagStore, userStore, meetingStore} = props;
  const { t } = useTranslation();
  const isProcessed = [DataType.Deliverable, DataType.Question].includes(data.data_type) && (data as Question).processed;
  const isRelevant  = [DataType.Deliverable, DataType.Question].includes(data.data_type) && (data as Question).isRelevantForMeeting;
  const progress    = [DataType.Deliverable].includes(data.data_type) && (data as Deliverable).progress;
  const {title, date, number} = data;
  
  const isPast: boolean  = isBefore(date, new Date());
  const formattedDueDate = format(new Date(date), "yyyy-MM-dd");
  const tags:   Tag[]    = tagStore.getItemTags(data.id);
  return (
    <CardBox type={type} onClick={onClick} cols={1}>
      <CardContent>
        <CardHeader>
        <CardHeading>{title}</CardHeading>
        </CardHeader>
        {meetingStore.getActiveMeeting && isRelevant && <StyledIcon size="large" color={isProcessed ? `${theme.color.status.priority}` : `${theme.color.grayLight}` } iconId="meeting" /> }
      </CardContent>
      <CardTags>
        {
          tags.map((t: ITag) => (
            <TagChip key={t.id} background={t.color || theme.color.grayDefault}>
              <Text 
                as    = "span"
                color = {getTextColorFromBackgroundColor(t.color || theme.color.grayDefault)}
                size  = "xxsmall"
              >
                #{t.title}
              </Text>
            </TagChip>
          ))
        }
      </CardTags>
      <CardActions isDeliverable={type === DataType.Deliverable}>
        <Column>
          <StatusAvatar variant="itemCard" data={data}/>
          <CardNumber>#{number}</CardNumber>
          <Tooltip placement="top" content={t("total_questions_linked")}>
            <Icon size="medium" iconId="chat" />
          </Tooltip>
          {(data.total_comments > 0) && (
            <Tooltip placement="bottom" content={t("total_comments") + ": " + data.total_comments}>
              <Icon size="medium" iconId="message" />
            </Tooltip>
          )}
          {(data.total_files > 0) && (
            <Tooltip content={t("total_files") + ": " + data.total_files}>
              <Icon size="medium" iconId="attachment" />
            </Tooltip>
          )}
          <Tooltip placement="top" content={t("total_deliverables_linked")}>
            <Icon size="medium" iconId="calendar" />
          </Tooltip>
          <CardDate color={isPast ? theme.font.color.red : theme.font.color.slate}>{formattedDueDate}</CardDate>
          {type === DataType.Deliverable &&
          <div style={{width:"2.35rem", height:"2.35rem"}}>
          <CircularProgressbarWithChildren
            styles = {buildStyles({
            strokeLinecap:  "butt",
            pathColor:     `${theme.color.blueA8}`,
            textColor:     `${theme.color.slate11}`,
            textSize:      "12px"
          })}
            value={progress}
          >
            <span style={{fontSize: 6}}>{progress}%</span>
          </CircularProgressbarWithChildren>
          </div>
          }
        </Column>

        {type == DataType.Question && (
          <Column>
            {("from" in data) && data.from && (
            <Tooltip content={userStore.getItemByID(data.from)?.displayName} placement="top">
              <Avatar size="input" image={userStore.getItemByID(data.from)?.getAvatar} backgroundColor={theme.color.grayDark} initials={userStore.getItemByID(data.from)?.getInitials} hoverInitials />
            </Tooltip>
            )}
            <Icon iconId="arrowForward" />
            {("to" in data) && data.to &&
            <Tooltip content={userStore.getItemByID(data.to)?.displayName} placement="top">
              <Avatar size="input"  image={userStore.getItemByID(data.to)?.getAvatar} backgroundColor={theme.color.grayDark} initials={userStore.getItemByID(data.to)?.getInitials} hoverInitials />
            </Tooltip>}
          </Column>
        )}

        {type == DataType.Deliverable && (
          <Column>
            {("to" in data) && data.to  &&
            <Tooltip content={userStore.getItemByID(data.to)?.displayName} placement="top">
              <Avatar size="input" backgroundColor={theme.color.grayDark} image={userStore.getItemByID(data.to)?.getAvatar} initials={userStore.getItemByID(data.to)?.getInitials} hoverInitials />
            </Tooltip>
            }
          </Column>
        )}
      </CardActions>
    </CardBox>
  );
}));

const TagChip = styled.div<{background: string}>`
  border-radius: 4px;
  padding:       0.25rem 0.5rem;
  margin-bottom: 0.5rem;
  margin-right:  0.5rem;
  background:    ${props => props.background};
  height:        fit-content;
`;

const CardTags = styled.div`
  display:        flex;
  flex-direction: row;
  flex-wrap:      wrap;
  padding:        0.25rem 0.5rem;
`;

const CardActions = styled.div<{isDeliverable: boolean;}>`
  align-items:           center;
  background:            ${theme.color.cards.delivFooter};
  border-radius:         0rem 0rem 0.4rem 0.4rem;
  border-top:            1px solid ${theme.color.cards.delivBorder};
  color:                 ${(props) => (props.color ? "red" : "black")};
  cursor:                pointer;
  display:               flex;
  flex-direction:        row;
  flex:                  1;
  font-size:             ${theme.font.size.small};
  grid-template-columns: repeat(6, 1fr);
  justify-content:       space-between;
  padding:               ${(p) => p.isDeliverable ? "0.2rem 0.4rem" : "0.4rem 0.4rem"};
`;

const cardStyles: { [type: string]: FlattenSimpleInterpolation } = {
  [DataType.Deliverable as string]: css`
    background-color: ${theme.color.cards.deliverable};
    border:  1px solid ${theme.color.cards.delivBorder};
  `,
  [DataType.Question as string]: css`
    background-color: ${theme.color.cards.question};
    border:  1px solid ${theme.color.cards.questionBorder};

    ${CardActions} {
      background-color: ${theme.color.cards.questionFooter};
      border-top: 1px solid ${theme.color.cards.questionBorder};
    }
  `,
  [DataType.Milestone as string]: css`
    background-color: ${theme.color.cards.milestone};
    border:  1px solid ${theme.color.cards.milestoneBorder};

    ${CardActions} {
      background-color: ${theme.color.cards.milestoneFooter};
      border-top: 1px solid ${theme.color.cards.milestoneBorder};
    }
  `,
  [DataType.Meeting as string]: css`
    background-color: ${theme.color.cards.meeting};
    border:  1px solid ${theme.color.cards.meetingBorder};

    ${CardActions} {
      background-color: ${theme.color.cards.meetingFooter};
      border-top: 1px solid ${theme.color.cards.meetingBorder};
    }
  `
};


const CardBox = styled(Box)<{ type?: DataType }>`
  border-radius:  0.4rem;
  border:         1px solid ${theme.color.cards.questionBorder};
  cursor:         pointer;
  display:        flex;
  flex-direction: column;
  width:          28rem;
  min-height:     14rem;
  ${(props) =>
    props.type ? cardStyles[props.type] : cardStyles[DataType.Question]};
  &:hover {
    /* transform:  scale(1.01); */
    /* transition: scale(0.2s) ease-in-out; */
    box-shadow: ${theme.boxShadow.hover};
  }
`;

const StyledIcon = styled(Icon)`
  align-self: flex-start;
`

const CardHeader = styled.div`
  display:        flex;
  flex-direction: column;
`

const Column = styled.div`
  display:        flex;
  align-items:    center;
  flex-direction: row;
  position:       relative;
  gap:            0.6rem;
`;

const CardHeading = styled.h4`
  font-size:   ${theme.font.size.xsmall};
  font-weight: ${theme.font.weight.normal};
`;

const CardContent = styled.div`
  display:        flex;
  justify-content: space-between;
  flex:           6;
  font-size:      ${theme.font.size.small};
  gap:            1.2rem;
  min-width:      20rem;
  padding:        0.6rem;
  width:          100%;
`;

const CardDate = styled.span`
  color:     ${(props) => props.color || theme.color.slate11};
  font-size: ${theme.font.size.xsmall};
  width:     fit-content;
`;

const CardNumber = styled.span`
  font-size:     ${theme.font.size.xsmall};
  font-weight:   ${theme.font.weight.bold};
`;

const StyledCardGrid = styled(Box)`
  flex-wrap:      wrap;
  display:        flex;
  flex-direction: row;
`;

type ICardGridProps = {
  children?: React.ReactNode;
  className?: string;
};

export const CardGrid: FC<ICardGridProps> = ({
  children,
  className
}: ICardGridProps): JSX.Element => {
  return (
    <StyledCardGrid
      breakpoints   = {[0, 1000, 1200, 1400]}
      colspan       = {12}
      paddingTop    = {"2.5rem"}
      paddingBottom = {"1rem"}
      gridRowGap    = {"1.5rem"}
      gridColumnGap = {"1.5rem"}
      className     = {className}
    >
      {React.Children.map(children, (child) => child)}
    </StyledCardGrid>
  );
};
